import { useEffect } from 'react'

import { Box, Icon, Link, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import { useSendInvitation } from 'modules/employees/hooks/useSendInvitation'
import type Employee from 'modules/employees/store/models/Employee'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

interface Props {
  employee: Employee
}

const InvitationNotSentOrAccepted = ({ employee }: Props) => {
  useEffect(() => {
    policyAuthorizations.authorizePolicies('employee.invite', [employee])
  }, [employee])

  const {
    invitationSent,
    invitationSentToUsers,
    performAction: sendInvitation,
  } = useSendInvitation(employee)

  const user = employeeUser(employee)
  const alreadyInvited = user.invited
  const alreadyJoined = user.confirmed

  const canInvite = policyAuthorizations.for('employee.invite', employee)
  const canShareProfileEmail = employee.policy.can('shareProfileEmail')
  const hasEmail = !!user.attributes.email
  const isNotTerminated = !employee.isTerminated

  const isVisible =
    canShareProfileEmail &&
    hasEmail &&
    canInvite &&
    isNotTerminated &&
    ((alreadyInvited && !alreadyJoined) || (!alreadyInvited && !alreadyJoined))

  if (!isVisible) return null

  const currentlyInvited = invitationSentToUsers.some((id) => employee.attributes.access_id === id)

  const isInvited = employeeUser(employee).invited || currentlyInvited

  const invitedAt = currentlyInvited ? moment() : moment(employeeUser(employee).invited_on)

  const title = () => {
    if (isInvited) {
      return i18n.t('employees.creation.invitation_not_accepted.title', {
        employee_name: employeeUser(employee).preferredName,
      })
    }

    return i18n.t('employees.creation.invitation_not_received', {
      employee_name: employeeUser(employee).preferredName,
    })
  }

  return (
    <Box paddingBottom="s32" paddingX="s64">
      <Box
        borderRadius={{ all: 'abs016' }}
        background="champagne200"
        padding="s12"
        paddingLeft="s16"
        flexDirection="row"
        justifyContent="spaceBetween"
        alignItems="center"
      >
        <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" width="full">
          <Box gap="s12" flexDirection="row" alignItems="center" paddingRight="s64">
            <Box background="champagne1200" borderRadius={{ all: 'abs008' }} padding="s2">
              <Icon.Medium icon="flag" color="grey000White" />
            </Box>
            <Box>
              <Text.Normal weight="medium">{title()}</Text.Normal>
              {isInvited && (
                <Text.Normal weight="medium">
                  {i18n.t('employees.creation.invitation_not_accepted.subtitle', {
                    invitation_date: dateToString(invitedAt, 'ui'),
                  })}
                </Text.Normal>
              )}
            </Box>
          </Box>

          <Box flexNoShrink>
            <Link.Touchable
              disabled={invitationSent}
              onClick={() => !invitationSent && sendInvitation()}
            >
              {isInvited ? i18n.t('employees.reinvite') : i18n.t('employees.invite')}
            </Link.Touchable>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default observer(InvitationNotSentOrAccepted)
